import type { Config } from "tailwindcss";
const contentPath = "./lib/**/*.{ts,tsx}";

export const tailwindPreset: Config = {
  content: [contentPath],
  safelist: [{ pattern: /^line-clamp-[0-9]+$/ }],
  theme: {
    colors: {
      neutral: {
        "50": "rgba(246, 248, 251)",
        "100": "rgba(236, 239, 244)",
        "200": "rgba(222, 228, 237)",
        "300": "rgba(203, 212, 225)",
        "400": "rgba(182, 194, 211)",
        "500": "rgba(154, 168, 188)",
        "600": "rgba(114, 129, 151)",
        "700": "rgba(82, 94, 111)",
        "800": "rgba(59, 69, 84)",
        "900": "rgba(39, 49, 63)",
        "925": "rgba(26, 33, 43)",
        "950": "rgba(28, 37, 50)",
        "975": "rgba(17, 22, 31)",
        white: "rgba(255, 255, 255)",
        black: "rgba(0, 0, 0)",
      },
      "stacklet-orange": {
        "50": "rgba(255, 238, 230)",
        "100": "rgba(255, 212, 189)",
        "200": "rgba(255, 185, 148)",
        "300": "rgba(255, 159, 107)",
        "400": "rgba(255, 130, 63)",
        "500": "rgba(255, 106, 26)",
        "600": "rgba(255, 89, 0)",
        "700": "rgba(199, 69, 0)",
        "800": "rgba(158, 55, 0)",
        "900": "rgba(117, 41, 0)",
        "950": "rgba(77, 27, 0)",
      },
      informative: {
        "50": "rgba(242, 247, 255)",
        "100": "rgba(224, 237, 255)",
        "200": "rgba(189, 218, 255)",
        "300": "rgba(134, 184, 254)",
        "400": "rgba(85, 152, 246)",
        "500": "rgba(45, 122, 229)",
        "600": "rgba(16, 95, 206)",
        "700": "rgba(0, 74, 177)",
        "800": "rgba(0, 59, 141)",
        "900": "rgba(0, 41, 98)",
        "950": "rgba(0, 21, 50)",
      },
      positive: {
        "50": "rgba(244, 255, 242)",
        "100": "rgba(225, 252, 222)",
        "200": "rgba(205, 249, 202)",
        "300": "rgba(164, 243, 163)",
        "400": "rgba(126, 235, 131)",
        "500": "rgba(66, 215, 91)",
        "600": "rgba(23, 191, 51)",
        "700": "rgba(4, 155, 34)",
        "800": "rgba(1, 106, 28)",
        "900": "rgba(0, 80, 22)",
        "950": "rgba(0, 48, 15)",
      },
      warning: {
        "50": "rgba(255, 251, 242)",
        "100": "rgba(255, 242, 210)",
        "200": "rgba(255, 232, 177)",
        "300": "rgba(255, 207, 115)",
        "400": "rgba(252, 179, 59)",
        "500": "rgba(242, 147, 13)",
        "600": "rgba(214, 111, 0)",
        "700": "rgba(186, 89, 0)",
        "800": "rgba(148, 60, 0)",
        "900": "rgba(102, 33, 0)",
        "950": "rgba(52, 13, 0)",
      },
      negative: {
        "50": "rgba(255, 242, 243)",
        "100": "rgba(255, 212, 216)",
        "200": "rgba(255, 182, 188)",
        "300": "rgba(250, 125, 135)",
        "400": "rgba(241, 74, 88)",
        "500": "rgba(228, 33, 49)",
        "600": "rgba(206, 7, 24)",
        "700": "rgba(177, 0, 15)",
        "800": "rgba(141, 0, 12)",
        "900": "rgba(98, 0, 8)",
        "950": "rgba(50, 0, 4)",
      },
      "data-viz": {
        pink: {
          "50": "rgba(255, 242, 249)",
          "100": "rgba(252, 208, 234)",
          "200": "rgba(248, 176, 218)",
          "300": "rgba(240, 117, 189)",
          "400": "rgba(230, 72, 164)",
          "500": "rgba(215, 36, 141)",
          "600": "rgba(194, 11, 118)",
          "700": "rgba(168, 0, 98)",
          "800": "rgba(134, 0, 78)",
          "900": "rgba(93, 0, 54)",
          "950": "rgba(47, 0, 28)",
        },
        violet: {
          "50": "rgba(250, 242, 255)",
          "100": "rgba(234, 215, 244)",
          "200": "rgba(218, 190, 234)",
          "300": "rgba(191, 140, 218)",
          "400": "rgba(164, 99, 199)",
          "500": "rgba(144, 62, 188)",
          "600": "rgba(124, 32, 173)",
          "700": "rgba(102, 12, 150)",
          "800": "rgba(81, 7, 121)",
          "900": "rgba(56, 3, 85)",
          "950": "rgba(28, 1, 43)",
        },
        indigo: {
          "50": "rgba(242, 246, 255)",
          "100": "rgba(217, 228, 252)",
          "200": "rgba(192, 211, 249)",
          "300": "rgba(140, 174, 242)",
          "400": "rgba(95, 140, 230)",
          "500": "rgba(60, 111, 214)",
          "600": "rgba(29, 84, 194)",
          "700": "rgba(8, 61, 167)",
          "800": "rgba(5, 48, 134)",
          "900": "rgba(2, 33, 93)",
          "950": "rgba(1, 16, 47)",
        },
        blue: {
          "50": "rgba(242, 252, 255)",
          "100": "rgba(215, 247, 255)",
          "200": "rgba(188, 242, 255)",
          "300": "rgba(132, 226, 255)",
          "400": "rgba(81, 208, 251)",
          "500": "rgba(41, 185, 233)",
          "600": "rgba(13, 160, 210)",
          "700": "rgba(0, 135, 180)",
          "800": "rgba(0, 107, 143)",
          "900": "rgba(0, 74, 99)",
          "950": "rgba(0, 38, 50)",
        },
        jade: {
          "50": "rgba(242, 255, 248)",
          "100": "rgba(212, 251, 231)",
          "200": "rgba(183, 246, 215)",
          "300": "rgba(128, 237, 182)",
          "400": "rgba(81, 224, 152)",
          "500": "rgba(44, 206, 125)",
          "600": "rgba(17, 187, 102)",
          "700": "rgba(1, 162, 82)",
          "800": "rgba(1, 130, 65)",
          "900": "rgba(0, 90, 45)",
          "950": "rgba(0, 46, 23)",
        },
        green: {
          "50": "rgba(249, 255, 242)",
          "100": "rgba(234, 248, 214)",
          "200": "rgba(219, 241, 188)",
          "300": "rgba(190, 230, 136)",
          "400": "rgba(163, 214, 92)",
          "500": "rgba(140, 201, 54)",
          "600": "rgba(117, 183, 26)",
          "700": "rgba(95, 159, 7)",
          "800": "rgba(76, 127, 4)",
          "900": "rgba(53, 89, 2)",
          "950": "rgba(26, 45, 0)",
        },
        yellow: {
          "50": "rgba(255, 252, 242)",
          "100": "rgba(255, 248, 211)",
          "200": "rgba(255, 244, 178)",
          "300": "rgba(255, 229, 115)",
          "400": "rgba(255, 212, 57)",
          "500": "rgba(255, 194, 8)",
          "600": "rgba(228, 172, 0)",
          "700": "rgba(195, 146, 0)",
          "800": "rgba(154, 116, 0)",
          "900": "rgba(107, 80, 0)",
          "950": "rgba(54, 41, 0)",
        },
        orange: {
          "50": "rgba(255, 246, 242)",
          "100": "rgba(255, 229, 215)",
          "200": "rgba(255, 212, 188)",
          "300": "rgba(255, 173, 132)",
          "400": "rgba(251, 137, 81)",
          "500": "rgba(233, 105, 41)",
          "600": "rgba(210, 78, 13)",
          "700": "rgba(180, 60, 0)",
          "800": "rgba(143, 48, 0)",
          "900": "rgba(99, 33, 0)",
          "950": "rgba(50, 17, 0)",
        },
        red: {
          "50": "rgba(255, 242, 244)",
          "100": "rgba(255, 216, 224)",
          "200": "rgba(255, 189, 203)",
          "300": "rgba(255, 133, 158)",
          "400": "rgba(252, 82, 117)",
          "500": "rgba(234, 42, 81)",
          "600": "rgba(210, 14, 54)",
          "700": "rgba(181, 0, 37)",
          "800": "rgba(144, 0, 29)",
          "900": "rgba(100, 0, 20)",
          "950": "rgba(50, 0, 10)",
        },
        "soft-indigo": "rgba(var(--data-viz-soft-indigo))",
        "bright-indigo": "rgba(var(--data-viz-bright-indigo))",
        "soft-violet": "rgba(var(--data-viz-soft-violet))",
        "bright-violet": "rgba(var(--data-viz-bright-violet))",
        "soft-pink": "rgba(var(--data-viz-soft-pink))",
        "bright-pink": "rgba(var(--data-viz-bright-pink))",
        "soft-red": "rgba(var(--data-viz-soft-red))",
        "bright-red": "rgba(var(--data-viz-bright-red))",
        "soft-orange": "rgba(var(--data-viz-soft-orange))",
        "bright-orange": "rgba(var(--data-viz-bright-orange))",
        "soft-yellow": "rgba(var(--data-viz-soft-yellow))",
        "bright-yellow": "rgba(var(--data-viz-bright-yellow))",
        "soft-green": "rgba(var(--data-viz-soft-green))",
        "bright-green": "rgba(var(--data-viz-bright-green))",
        "soft-jade": "rgba(var(--data-viz-soft-jade))",
        "bright-jade": "rgba(var(--data-viz-bright-jade))",
        "soft-blue": "rgba(var(--data-viz-soft-blue))",
        "bright-blue": "rgba(var(--data-viz-bright-blue))",
        "soft-grey": "rgba(var(--data-viz-soft-grey))",
        "bright-grey": "rgba(var(--data-viz-bright-grey))",
      },
      background: {
        primary: "rgba(var(--background-primary))",
        secondary: "rgba(var(--background-secondary))",
        tertiary: "rgba(var(--background-tertiary))",
        "primary-inverse": "rgba(var(--background-primary-inverse))",
        "secondary-inverse": "rgba(var(--background-secondary-inverse))",
        brand: "rgba(var(--background-brand))",
        informative: "rgba(var(--background-informative))",
        positive: "rgba(var(--background-positive))",
        warning: "rgba(var(--background-warning))",
        negative: "rgba(var(--background-negative))",
      },
      text: {
        primary: "rgba(var(--text-primary))",
        secondary: "rgba(var(--text-secondary))",
        "primary-inverse": "rgba(var(--text-primary-inverse))",
        "secondary-inverse": "rgba(var(--text-secondary-inverse))",
        brand: "rgba(var(--text-brand))",
        informative: "rgba(var(--text-informative))",
        positive: "rgba(var(--text-positive))",
        warning: "rgba(var(--text-warning))",
        negative: "rgba(var(--text-negative))",
        disabled: "rgba(var(--text-disabled))",
        tag: "rgba(var(--text-tag))",
      },
      foreground: {
        primary: "rgba(var(--foreground-primary))",
        secondary: "rgba(var(--foreground-secondary))",
        brand: "rgba(var(--foreground-brand))",
        informative: "rgba(var(--foreground-informative))",
        positive: "rgba(var(--foreground-positive))",
        warning: "rgba(var(--foreground-warning))",
        negative: "rgba(var(--foreground-negative))",
        disabled: "rgba(var(--foreground-disabled))",
        tag: "rgba(var(--foreground-tag))",
      },
      outline: {
        brand: "rgba(var(--outline-brand))",
        decorative: "rgba(var(--outline-decorative))",
        informative: "rgba(var(--outline-informative))",
        positive: "rgba(var(--outline-positive))",
        warning: "rgba(var(--outline-warning))",
        negative: "rgba(var(--outline-negative))",
        input: {
          enabled: "rgba(var(--outline-input-enabled))",
          hover: "rgba(var(--outline-input-hover))",
          focus: "rgba(var(--outline-input-focus))",
          disabled: "rgba(var(--outline-input-disabled))",
        },
      },
      interactive: {
        link: {
          enabled: "rgba(var(--interactive-link-enabled))",
          hover: "rgba(var(--interactive-link-hover))",
          focus: "rgba(var(--interactive-link-focus))",
          disabled: "rgba(var(--interactive-link-disabled))",
        },
        "primary-button": {
          "enabled-background": "rgba(var(--interactive-primary-button-enabled-background))",
          "enabled-foreground": "rgba(var(--interactive-primary-button-enabled-foreground))",
          "hover-background": "rgba(var(--interactive-primary-button-hover-background))",
          "hover-foreground": "rgba(var(--interactive-primary-button-hover-foreground))",
          "focus-background": "rgba(var(--interactive-primary-button-focus-background))",
          "focus-foreground": "rgba(var(--interactive-primary-button-focus-foreground))",
        },
        "secondary-button": {
          "enabled-background": "rgba(var(--interactive-secondary-button-enabled-background))",
          "enabled-foreground": "rgba(var(--interactive-secondary-button-enabled-foreground))",
          "enabled-outline": "rgba(var(--interactive-secondary-button-enabled-outline))",
          "hover-background": "rgba(var(--interactive-secondary-button-hover-background))",
          "hover-foreground": "rgba(var(--interactive-secondary-button-hover-foreground))",
          "hover-outline": "rgba(var(--interactive-secondary-button-hover-outline))",
          "focus-background": "rgba(var(--interactive-secondary-button-focus-background))",
          "focus-foreground": "rgba(var(--interactive-secondary-button-focus-foreground))",
          "focus-outline": "rgba(var(--interactive-secondary-button-focus-outline))",
        },
        "tertiary-button": {
          "enabled-background": "rgba(var(--interactive-tertiary-button-enabled-background))",
          "enabled-foreground": "rgba(var(--interactive-tertiary-button-enabled-foreground))",
          "enabled-outline": "rgba(var(--interactive-tertiary-button-enabled-outline))",
          "hover-background": "rgba(var(--interactive-tertiary-button-hover-background))",
          "hover-foreground": "rgba(var(--interactive-tertiary-button-hover-foreground))",
          "hover-outline": "rgba(var(--interactive-tertiary-button-hover-outline))",
          "focus-background": "rgba(var(--interactive-tertiary-button-focus-background))",
          "focus-foreground": "rgba(var(--interactive-tertiary-button-focus-foreground))",
          "focus-outline": "rgba(var(--interactive-tertiary-button-focus-outline))",
        },
        "glass-button": {
          "enabled-background": "rgba(var(--interactive-glass-button-enabled-background))",
          "enabled-foreground": "rgba(var(--interactive-glass-button-enabled-foreground))",
          "enabled-outline": "rgba(var(--interactive-glass-button-enabled-outline))",
          "hover-background": "rgba(var(--interactive-glass-button-hover-background))",
          "hover-foreground": "rgba(var(--interactive-glass-button-hover-foreground))",
          "hover-outline": "rgba(var(--interactive-glass-button-hover-outline))",
          "focus-background": "rgba(var(--interactive-glass-button-focus-background))",
          "focus-foreground": "rgba(var(--interactive-glass-button-focus-foreground))",
          "focus-outline": "rgba(var(--interactive-glass-button-focus-outline))",
        },
        "negative-button": {
          "enabled-background": "rgba(var(--interactive-negative-button-enabled-background))",
          "enabled-foreground": "rgba(var(--interactive-negative-button-enabled-foreground))",
          "hover-background": "rgba(var(--interactive-negative-button-hover-background))",
          "hover-foreground": "rgba(var(--interactive-negative-button-hover-foreground))",
          "focus-background": "rgba(var(--interactive-negative-button-focus-background))",
          "focus-foreground": "rgba(var(--interactive-negative-button-focus-foreground))",
        },
        disabled: {
          background: "rgba(var(--interactive-disabled-background))",
          foreground: "rgba(var(--interactive-disabled-foreground))",
          outline: "rgba(var(--interactive-disabled-outline))",
        },
      },
      overlay: {
        light: "rgba(var(--overlay-light))",
        medium: "rgba(var(--overlay-medium))",
        dark: "rgba(var(--overlay-dark))",
        brand: "rgba(var(--overlay-brand))",
        informative: "rgba(var(--overlay-informative))",
        positive: "rgba(var(--overlay-positive))",
        warning: "rgba(var(--overlay-warning))",
        negative: "rgba(var(--overlay-negative))",
      },
      brand: {
        primary: "rgba(var(--brand-primary))",
        secondary: "rgba(var(--brand-secondary))",
        tertiary: "rgba(var(--brand-tertiary))",
      },
      "data-grid": {
        "section-header": "rgba(var(--data-grid-section-header))",
        "row-selected": "rgba(var(--data-grid-row-selected))",
        "row-even": "rgba(var(--data-grid-row-even))",
        "row-odd": "rgba(var(--data-grid-row-odd))",
        "row-hover": "rgba(var(--data-grid-row-hover))",
      },
      // Legacy colors from theme.ts- add here as needed to avoid inline hex values and the styles prop
      legacy: {
        // The navy + neutrals greyscale ramps below resolve to CSS variables
        // (lib/styles/mode.css): original hex in light, mirrored ramp in dark.
        // This themes the ~60 legacy call sites without touching each file. The
        // orange/red/green ramps stay literal — brand/status colors that read
        // correctly on both themes.
        navy: {
          base: "rgba(var(--legacy-navy-base))",
          L10: "rgba(var(--legacy-navy-L10))",
          L20: "rgba(var(--legacy-navy-L20))",
          L30: "rgba(var(--legacy-navy-L30))",
          L40: "rgba(var(--legacy-navy-L40))",
          L50: "rgba(var(--legacy-navy-L50))",
          L70: "rgba(var(--legacy-navy-L70))",
          L80: "rgba(var(--legacy-navy-L80))",
          L90: "rgba(var(--legacy-navy-L90))",
          L95: "rgba(var(--legacy-navy-L95))",
          D50: "rgba(var(--legacy-navy-D50))",
        },
        orange: {
          base: "#C05621",
          D10: "#B6521F",
          D20: "#A3491C",
          D30: "#863C17",
          L20: "#C96F42",
          L60: "#E7BEAA",
          L70: "#ECCCBC",
        },
        "dark-orange": {
          D30: "#6D2E17",
        },
        green: {
          D30: "#27714A",
        },
        red: {
          D20: "#C33535",
          D30: "#A02B2B",
          D40: "#7E2222",
          L50: "#F5B2B2",
        },
        neutrals: {
          eerieBlack: "rgba(var(--legacy-neutrals-eerie-black))",
          ash: "rgba(var(--legacy-neutrals-ash))",
          denseFog: "rgba(var(--legacy-neutrals-dense-fog))",
          fog: "rgba(var(--legacy-neutrals-fog))",
          stormyGray: "rgba(var(--legacy-neutrals-stormy-gray))",
        },
      },
      transparent: "transparent",
    },
    borderRadius: {
      none: "0",
      "1": "var(--radius-1)",
      "2": "var(--radius-2)",
      "3": "var(--radius-3)",
      "4": "var(--radius-4)",
      "5": "var(--radius-5)",
      "6": "var(--radius-6)",
      "7": "var(--radius-7)",
      "8": "var(--radius-8)",
      "9": "var(--radius-9)",
      "10": "var(--radius-10)",
      xs: "var(--radius-xs)",
      sm: "var(--radius-sm)",
      md: "var(--radius-md)",
      lg: "var(--radius-lg)",
      xl: "var(--radius-xl)",
      full: "9999px",
    },
    fontSize: {
      "page-headline": [
        "var(--size-xl--24-)",
        {
          fontWeight: 600,
          lineHeight: "100%",
          letterSpacing: "0em",
        },
      ],
      "display-large": [
        "var(--size-5xl--64-)",
        {
          fontWeight: 600,
          lineHeight: "113%",
          letterSpacing: "0em",
        },
      ],
      "display-medium": [
        "var(--size-4xl--52-)",
        {
          fontWeight: 600,
          lineHeight: "112%",
          letterSpacing: "0em",
        },
      ],
      "display-small": [
        "var(--size-3xl--44-)",
        {
          fontWeight: 600,
          lineHeight: "114%",
          letterSpacing: "0em",
        },
      ],
      "title-large": [
        "var(--size-xl--24-)",
        {
          fontWeight: 700,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "title-medium": [
        "var(--size-lg--16-)",
        {
          fontWeight: 700,
          lineHeight: "125%",
          letterSpacing: "0em",
        },
      ],
      "title-small": [
        "var(--size-md--14-)",
        {
          fontWeight: 700,
          lineHeight: "129%",
          letterSpacing: "0em",
        },
      ],
      "body-large": [
        "var(--size-lg--16-)",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "body-medium": [
        "var(--size-md--14-)",
        {
          fontWeight: 400,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "body-small": [
        "var(--size-sm--12-)",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "body-large-strong": [
        "1rem",
        {
          fontWeight: 700,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "body-medium-strong": [
        "var(--size-md--14-)",
        {
          fontWeight: 700,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "body-small-strong": [
        "var(--size-sm--12-)",
        {
          fontWeight: 700,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "link-large": [
        "1rem",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "link-medium": [
        "var(--size-md--14-)",
        {
          fontWeight: 400,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "link-small": [
        "var(--size-sm--12-)",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "label-large": [
        "1rem",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "label-medium": [
        "var(--size-md--14-)",
        {
          fontWeight: 400,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "label-small": [
        "var(--size-sm--12-)",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "label-extra-small": [
        "var(--size-xs--10-)",
        {
          fontWeight: 400,
          lineHeight: "160%",
          letterSpacing: "0em",
        },
      ],
      "label-large-strong": [
        "1rem",
        {
          fontWeight: 600,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "label-medium-strong": [
        "var(--size-md--14-)",
        {
          fontWeight: 600,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "label-small-strong": [
        "var(--size-sm--12-)",
        {
          fontWeight: 600,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
      "label-extra-small-strong": [
        "var(--size-xs--10-)",
        {
          fontWeight: 700,
          lineHeight: "180%",
          letterSpacing: "0em",
        },
      ],
      "mono-medium": [
        "var(--size-md--14-)",
        {
          fontWeight: 400,
          lineHeight: "143%",
          letterSpacing: "0em",
        },
      ],
      "mono-small": [
        "var(--size-sm--12-)",
        {
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0em",
        },
      ],
    },
    lineHeight: {
      "4xl": "var(--line-height-4xl--72-)",
      "3xl--58-": "var(--line-height-3xl--58-)",
      "2xl": "var(--line-height-2xl--50-)",
      xs: "var(--line-height-xs--16-)",
      xl: "var(--line-height-xl--36-)",
      lg: "var(--line-height-lg--24-)",
      md: "var(--line-height-md--20-)",
      sm: "var(--line-height-sm--18-)",

      "display-small": "var(--font-line-height-display-small)",
      "display-medium": "var(--font-line-height-display-medium)",
      "display-large": "var(--font-line-height-display-large)",
      "label-extra-small": "var(--font-line-height-label-extra-small)",
      "label-small": "var(--font-line-height-label-small)",
      "label-medium": "var(--font-line-height-label-medium)",
      "label-large": "var(--font-line-height-label-large)",
      "mono-small": "var(--font-line-height-mono-small)",
      "mono-medium": "var(--font-line-height-mono-medium)",
      "link-small": "var(--font-line-height-link-small)",
      "link-medium": "var(--font-line-height-link-medium)",
      "link-large": "var(--font-line-height-link-large)",
      "body-small": "var(--font-line-height-body-small)",
      "body-medium": "var(--font-line-height-body-medium)",
      "body-large": "var(--font-line-height-body-large)",
      "title-small": "var(--font-line-height-title-small)",
      "title-medium": "var(--font-line-height-title-medium)",
      "title-large": "var(--font-line-height-title-large)",
      "page-headline": "var(--font-line-height-page-headline)",
    },
    size: {
      "5xl": "var(--size-5xl--64-)",
      xs: "var(--size-xs--10-)",
      "4xl": "var(--size-4xl--52-)",
      sm: "var(--size-sm--12-)",
      "2xl": "var(--size-2xl--32-)",
      xl: "var(--size-xl--24-)",
      md: "var(--size-md--14-)",
      "3xl": "var(--size-3xl--44-)",
      lg: "var(--size-lg--16-)",
      "icon-xs": "var(--icon-size-xs)",
      "icon-s": "var(--icon-size-s)",
      "icon-m": "var(--icon-size-m)",
      "icon-l": "var(--icon-size-l)",
      "icon-xl": "var(--icon-size-xl)",
      "icon-2xl": "var(--icon-size-2xl)",
    },
    spacing: {
      "0": "var(--spacing-0)",
      px: "var(--spacing-px)",
      "0-5": "var(--spacing-0-5)",
      "1": "var(--spacing-1)",
      "1-5": "var(--spacing-1-5)",
      "2": "var(--spacing-2)",
      "2-5": "var(--spacing-2-5)",
      "3": "var(--spacing-3)",
      "3-5": "var(--spacing-3-5)",
      "4": "var(--spacing-4)",
      "5": "var(--spacing-5)",
      "6": "var(--spacing-6)",
      "7": "var(--spacing-7)",
      "8": "var(--spacing-8)",
      "9": "var(--spacing-9)",
      "10": "var(--spacing-10)",
      "11": "var(--spacing-11)",
      "12": "var(--spacing-12)",
      "14": "var(--spacing-14)",
      "16": "var(--spacing-16)",
      "20": "var(--spacing-20)",
      "24": "var(--spacing-24)",
      "28": "var(--spacing-28)",
      "32": "var(--spacing-32)",
      "36": "var(--spacing-36)",
      "40": "var(--spacing-40)",
      "44": "var(--spacing-44)",
      "48": "var(--spacing-48)",
      "52": "var(--spacing-52)",
      "56": "var(--spacing-56)",
      "60": "var(--spacing-60)",
      "64": "var(--spacing-64)",
      "72": "var(--spacing-72)",
      "80": "var(--spacing-80)",
      "96": "var(--spacing-96)",
      "3xs": "var(--space-3xs)",
      xxs: "var(--space-xxs)",
      xs: "var(--space-xs)",
      sm: "var(--space-sm)",
      md: "var(--space-md)",
      lg: "var(--space-lg)",
      xl: "var(--space-xl)",
      "2xl": "var(--space-2xl)",
      "3xl": "var(--space-3xl)",
      "4xl": "var(--space-4xl)",
      "5xl": "var(--space-5xl)",
      //// TODO: remove or find place for these
      breakpoint: "var(--breakpoint)",
      "content-width": "var(--content-width)",
      columns: "var(--columns)",
      gutter: "var(--gutter)",
      padding: "var(--padding)",
    },
    extend: {
      animation: {
        "loading-overlay-slide": "loading-overlay-slide 1.5s ease-in-out infinite",
        shimmer: "shimmer 2s infinite",
      },
      keyframes: {
        "loading-overlay-slide": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(300%)" },
        },
        shimmer: {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(100%)" },
        },
      },
    },
  },
};
